<template>
  <div v-if="mounted">
    <CCard>
      <CCardBody>
        <div class="d-flex flex-wrap align-items-start justify-content-between" style="gap: 0.75rem;">
          <div>
            <h4 class="card-title mb-0">Ad Click Report</h4>
            <div class="small text-muted">
              {{
                isRawView
                  ? 'SuperAdmin overview of tracked ad clicks, campaign attribution and raw click identifiers'
                  : 'Grouped campaign performance with clicks, attributed orders and omzet'
              }}
            </div>
          </div>

          <div class="view-toggle">
            <CButton
              color="warning"
              :variant="isRawView ? '' : 'outline'"
              @click="switchView('raw')"
            >
              Raw Clicks
            </CButton>
            <CButton
              color="warning"
              :variant="isPerformanceView ? '' : 'outline'"
              @click="switchView('performance')"
            >
              Performance
            </CButton>
          </div>
        </div>

        <CCard class="filters my-3" accent-color="warning">
          <CCardBody class="p-2">
            <div class="grid">
              <CInput
                v-if="isRawView"
                class="mb-0"
                type="search"
                placeholder="Search in IP, path, referer, UTM, click ids..."
                v-model="filters.search"
              />

              <CInput
                class="mb-0"
                type="date"
                label="From"
                v-model="filters.start_date"
              />

              <CInput
                class="mb-0"
                type="date"
                label="To"
                v-model="filters.end_date"
              />

              <div class="form-group mb-0">
                <label>Sort field</label>
                <select
                  class="custom-select"
                  v-model="orderBy"
                  @change="orderFilterChange"
                >
                  <option :value="null">Default</option>
                  <option
                    v-for="option in orderFieldOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="form-group mb-0">
                <label>Sort direction</label>
                <select
                  class="custom-select"
                  v-model="direction"
                  @change="orderFilterChange"
                >
                  <option :value="null">Default</option>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>

              <CInput
                v-if="isRawView"
                class="mb-0"
                type="number"
                placeholder="Login user ID"
                v-model="filters.login_user_id"
              />

              <CInput
                v-if="isRawView"
                class="mb-0"
                placeholder="Session ID"
                v-model="filters.session_id"
              />

              <CInput
                v-if="isRawView"
                class="mb-0"
                placeholder="Order SID / ID"
                v-model="filters.linked_order"
              />

              <CInput
                class="mb-0"
                placeholder="Country code"
                v-model="filters.country_code"
              />

              <CInput
                class="mb-0"
                placeholder="Device type"
                v-model="filters.device_type"
              />

              <CInput
                v-if="isPerformanceView"
                class="mb-0"
                placeholder="Client channel (app/main/sub)"
                v-model="filters.client_channel"
              />

              <CInput
                v-if="isRawView"
                class="mb-0"
                placeholder="Google Ads campaign id"
                v-model="filters.gad_campaignid"
              />

              <CInput
                v-if="isRawView"
                class="mb-0"
                placeholder="UTM campaign"
                v-model="filters.utm_campaign"
              />

              <CInput
                v-if="isPerformanceView"
                class="mb-0"
                placeholder="Campaign key"
                v-model="filters.campaign_key"
              />

              <CInput
                v-if="isPerformanceView"
                class="mb-0"
                placeholder="Campaign id"
                v-model="filters.campaign_id"
              />

              <CInput
                v-if="isPerformanceView"
                class="mb-0"
                placeholder="Campaign name"
                v-model="filters.campaign_name"
              />

              <CInput
                class="mb-0"
                placeholder="UTM source"
                v-model="filters.utm_source"
              />

              <CInput
                class="mb-0"
                placeholder="UTM medium"
                v-model="filters.utm_medium"
              />

              <div class="d-flex align-items-end">
                <CButton color="warning" variant="outline" block @click="resetFilters">
                  Reset Filters
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>

        <CAlert v-if="errorMessage" show color="danger" class="mb-4">
          {{ errorMessage }}
        </CAlert>

        <div v-if="isRawView" class="row mb-4 stats-cards">
          <div class="col-sm-6 col-lg">
            <CWidgetDropdown color="primary" :header="`${rawStats.total_clicks || 0}`" text="Total Clicks" />
          </div>
          <div class="col-sm-6 col-lg">
            <CWidgetDropdown color="info" :header="`${rawStats.unique_session_id || 0}`" text="Unique Sessions" />
          </div>
          <div class="col-sm-6 col-lg">
            <CWidgetDropdown color="success" :header="`${rawStats.unique_login_user_id || 0}`" text="Logged-in Users" />
          </div>
          <div class="col-sm-6 col-lg">
            <CWidgetDropdown color="warning" :header="`${rawStats.unique_ip || 0}`" text="Unique IPs" />
          </div>
          <div class="col-sm-6 col-lg">
            <CWidgetDropdown color="dark" :header="`${rawStats.unique_country_code || 0}`" text="Countries" />
          </div>
        </div>

        <div v-else class="row mb-4 stats-cards">
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown color="primary" :header="formatInteger(performanceStats.total_clicks)" text="Clicks" />
          </div>
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown color="success" :header="formatInteger(performanceStats.total_attributed_orders)" text="Attributed Orders" />
          </div>
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown color="info" :header="formatCurrency(performanceStats.total_attributed_revenue)" text="Attributed Revenue" />
          </div>
          <div class="col-sm-6 col-lg-3">
            <CWidgetDropdown color="warning" :header="formatInteger(performanceStats.total_campaigns)" text="Campaign Groups" />
          </div>
        </div>

        <div
          v-if="isRawView"
          class="d-flex flex-wrap align-items-center justify-content-between mb-3 text-muted"
          style="gap: 0.75rem;"
        >
          <div>
            First click:
            <strong>{{ formatDateTime(rawStats.first_click_at) }}</strong>
          </div>
          <div>
            Last click:
            <strong>{{ formatDateTime(rawStats.last_click_at) }}</strong>
          </div>
        </div>

        <CAlert v-else show color="info" class="mb-4 small">
          Date filters use click date for clicks and order date for attributed orders and omzet.
          App deeplink payload rows can show 0 clicks because no app open or click event is stored in v1.
        </CAlert>

        <div class="table-responsive">
          <CDataTable
            striped
            hover
            :items="rows"
            :fields="visibleFields"
            :sorter="{ external: true, resetable: true }"
            :itemsPerPageSelect="{
              external: true,
              values: [15, 25, 50, 100, 250, 500],
            }"
            :items-per-page.sync="itemsPerPage"
            :loading="loading"
            :noItemsView="noItemsView"
            @update:sorter-value="sorterValue"
            @pagination-change="paginationChange"
          >
            <template #clicked_at="{ item }">
              <td>{{ formatDateTime(item.clicked_at) }}</td>
            </template>

            <template #login_user_id="{ item }">
              <td>
                <span v-if="item.login_user_id">#{{ item.login_user_id }}</span>
                <span v-else class="text-muted">Guest</span>
              </td>
            </template>

            <template #country_code="{ item }">
              <td>
                <CBadge :color="item.country_code ? 'info' : 'secondary'" shape="pill">
                  {{ item.country_code || 'N/A' }}
                </CBadge>
              </td>
            </template>

            <template #device_type="{ item }">
              <td>
                <CBadge :color="item.device_type ? 'dark' : 'secondary'" shape="pill">
                  {{ item.device_type || 'Unknown' }}
                </CBadge>
              </td>
            </template>

            <template #uri_path="{ item }">
              <td :title="item.uri_path || '-'">
                {{ truncate(item.uri_path) }}
              </td>
            </template>

            <template #referer="{ item }">
              <td :title="item.referer || '-'">
                {{ truncate(item.referer) }}
              </td>
            </template>

            <template #campaigns="{ item }">
              <td>
                <div v-if="item.gad_campaignid">
                  <strong>Ads:</strong> {{ item.gad_campaignid }}
                  <span v-if="item.gad_source" class="text-muted">({{ item.gad_source }})</span>
                </div>
                <div v-if="item.utm_campaign">
                  <strong>UTM:</strong> {{ item.utm_campaign }}
                </div>
                <div v-if="!item.gad_campaignid && !item.utm_campaign" class="text-muted">
                  -
                </div>
              </td>
            </template>

            <template #utm="{ item }">
              <td>
                <div v-if="item.utm_source || item.utm_medium">
                  <strong>{{ item.utm_source || '-' }}</strong>
                  <span class="text-muted">/ {{ item.utm_medium || '-' }}</span>
                </div>
                <div v-if="item.utm_term" class="text-muted">
                  term: {{ item.utm_term }}
                </div>
                <div v-if="item.utm_content" class="text-muted">
                  content: {{ truncate(item.utm_content, 48) }}
                </div>
                <div v-if="!item.utm_source && !item.utm_medium && !item.utm_term && !item.utm_content" class="text-muted">
                  -
                </div>
              </td>
            </template>

            <template #tracking_ids="{ item }">
              <td>
                <div v-if="item.tracking_ids.length" class="tracking-badges">
                  <CBadge
                    v-for="trackingId in item.tracking_ids"
                    :key="trackingId.key"
                    color="light"
                    class="tracking-badge"
                    :title="trackingId.value"
                  >
                    {{ trackingId.label }}: {{ truncate(trackingId.value, 18) }}
                  </CBadge>
                </div>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #linked_orders="{ item }">
              <td>
                <div v-if="item.linked_orders.length" class="linked-order-links">
                  <CLink
                    v-for="order in item.linked_orders"
                    :key="order.id"
                    :to="{ name: 'Order Details', params: { id: order.id } }"
                  >
                    {{ order.sid }}
                  </CLink>
                </div>
                <span v-else class="text-muted">-</span>
              </td>
            </template>

            <template #campaign_key="{ item }">
              <td>
                <CBadge :color="item.campaign_key === 'unlabeled' ? 'secondary' : 'warning'" shape="pill">
                  {{ item.campaign_key || 'unlabeled' }}
                </CBadge>
              </td>
            </template>

            <template #campaign_id="{ item }">
              <td>{{ item.campaign_id || '-' }}</td>
            </template>

            <template #campaign_name="{ item }">
              <td :title="item.campaign_name || '-'">
                {{ truncate(item.campaign_name, 48) }}
              </td>
            </template>

            <template #utm_source="{ item }">
              <td>{{ item.utm_source || '-' }}</td>
            </template>

            <template #utm_medium="{ item }">
              <td>{{ item.utm_medium || '-' }}</td>
            </template>

            <template #client_channel="{ item }">
              <td>
                <CBadge
                  :color="item.client_channel === 'app' ? 'success' : item.client_channel === 'sub' ? 'warning' : 'primary'"
                  shape="pill"
                >
                  {{ item.client_channel || 'main' }}
                </CBadge>
              </td>
            </template>

            <template #clicks="{ item }">
              <td class="text-right">{{ formatInteger(item.clicks) }}</td>
            </template>

            <template #attributed_orders="{ item }">
              <td class="text-right">{{ formatInteger(item.attributed_orders) }}</td>
            </template>

            <template #attributed_revenue="{ item }">
              <td class="text-right">{{ formatCurrency(item.attributed_revenue) }}</td>
            </template>

            <template #first_click_at="{ item }">
              <td>{{ formatDateTime(item.first_click_at) }}</td>
            </template>

            <template #last_click_at="{ item }">
              <td>{{ formatDateTime(item.last_click_at) }}</td>
            </template>

            <template #last_order_at="{ item }">
              <td>{{ formatDateTime(item.last_order_at) }}</td>
            </template>
          </CDataTable>
        </div>

        <CCard class="actions sticky-bottom">
          <CCardBody class="p-2">
            <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
              <div>
                <h5 class="mt-1">
                  {{ summaryLabel }}: <span class="d-inline count bg-primary pb-1">{{ total }}</span>
                </h5>
              </div>

              <div v-if="pages > 1" class="ml-auto">
                <CPagination align="center" :dots="false" :pages="pages" :active-page.sync="activePage" />
              </div>
            </div>
          </CCardBody>
        </CCard>

        <mc-spinner :opacity="0.8" v-show="loading" :mtop="(32 + mcSpinnerMarginTop) + 'px'" />
      </CCardBody>
    </CCard>
  </div>
  <CSpinner v-else color="primary" />
</template>

<script>
import _debounce from 'lodash/debounce';

const defaultRawStats = () => ({
  total_clicks: 0,
  unique_session_id: 0,
  unique_login_user_id: 0,
  unique_ip: 0,
  unique_country_code: 0,
  first_click_at: null,
  last_click_at: null,
});

const defaultPerformanceStats = () => ({
  total_clicks: 0,
  total_attributed_orders: 0,
  total_attributed_revenue: 0,
  total_campaigns: 0,
});

const defaultFilters = () => ({
  search: null,
  start_date: null,
  end_date: null,
  login_user_id: null,
  session_id: null,
  linked_order: null,
  country_code: null,
  device_type: null,
  gad_campaignid: null,
  utm_campaign: null,
  utm_source: null,
  utm_medium: null,
  campaign_key: null,
  campaign_id: null,
  campaign_name: null,
  client_channel: null,
});

export default {
  name: 'AdClickReport',
  data() {
    return {
      mounted: false,
      loading: false,
      errorMessage: null,
      activeView: this.$route.query.view === 'performance' ? 'performance' : 'raw',
      rows: [],
      availableColumns: [],
      timestampColumn: 'created_at',
      rawStats: defaultRawStats(),
      performanceStats: defaultPerformanceStats(),
      filters: defaultFilters(),
      activePage: parseInt(this.$route.query.page, 10) || 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      direction: null,
      mcSpinnerMarginTop: 0,
      rawFieldDefinitions: [
        { key: 'id', label: '#ID', column: 'id', _style: 'min-width: 90px;' },
        { key: 'clicked_at', label: 'Clicked At', column: 'clicked_at', sorter: true, _style: 'min-width: 170px;' },
        { key: 'login_user_id', label: 'User', column: 'login_user_id', _style: 'min-width: 110px;' },
        { key: 'session_id', label: 'Session', column: 'session_id', _style: 'min-width: 180px;' },
        { key: 'ip', label: 'IP', column: 'ip', _style: 'min-width: 130px;' },
        { key: 'country_code', label: 'Country', column: 'country_code', _style: 'min-width: 100px; text-align: center;' },
        { key: 'device_type', label: 'Device', column: 'device_type', _style: 'min-width: 110px; text-align: center;' },
        { key: 'uri_path', label: 'Path', sorter: false, _style: 'min-width: 180px;' },
        { key: 'referer', label: 'Referer', sorter: false, _style: 'min-width: 220px;' },
        { key: 'campaigns', label: 'Campaigns', sorter: false, _style: 'min-width: 200px;' },
        { key: 'utm', label: 'UTM', sorter: false, _style: 'min-width: 220px;' },
        { key: 'tracking_ids', label: 'Tracking IDs', sorter: false, _style: 'min-width: 260px;' },
        { key: 'linked_orders', label: 'Order', column: 'linked_order', sorter: true, _style: 'min-width: 120px;' },
      ],
      performanceFieldDefinitions: [
        { key: 'campaign_key', label: 'Campaign Key', column: 'campaign_key', sorter: true, _style: 'min-width: 160px;' },
        { key: 'campaign_id', label: 'Campaign ID', column: 'campaign_id', sorter: true, _style: 'min-width: 140px;' },
        { key: 'campaign_name', label: 'Campaign Name', column: 'campaign_name', sorter: true, _style: 'min-width: 180px;' },
        { key: 'utm_source', label: 'Source', column: 'utm_source', sorter: true, _style: 'min-width: 120px;' },
        { key: 'utm_medium', label: 'Medium', column: 'utm_medium', sorter: true, _style: 'min-width: 120px;' },
        { key: 'client_channel', label: 'Channel', column: 'client_channel', sorter: true, _style: 'min-width: 120px;' },
        { key: 'clicks', label: 'Clicks', column: 'clicks', sorter: true, _style: 'min-width: 110px;' },
        { key: 'attributed_orders', label: 'Orders', column: 'attributed_orders', sorter: true, _style: 'min-width: 110px;' },
        { key: 'attributed_revenue', label: 'Revenue', column: 'attributed_revenue', sorter: true, _style: 'min-width: 140px;' },
        { key: 'first_click_at', label: 'First Click', column: 'first_click_at', sorter: true, _style: 'min-width: 170px;' },
        { key: 'last_click_at', label: 'Last Click', column: 'last_click_at', sorter: true, _style: 'min-width: 170px;' },
        { key: 'last_order_at', label: 'Last Order', column: 'last_order_at', sorter: true, _style: 'min-width: 170px;' },
      ],
    };
  },

  computed: {
    isRawView() {
      return this.activeView === 'raw';
    },
    isPerformanceView() {
      return this.activeView === 'performance';
    },
    itemsPerPage: {
      get() {
        return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE, 10);
      },
      set(newValue) {
        this.items_per_page = newValue;
      },
    },
    reloadParams() {
      return [this.activePage, this.activeView];
    },
    queryPage() {
      return parseInt(this.$route.query.page, 10) || 1;
    },
    queryView() {
      return this.$route.query.view === 'performance' ? 'performance' : 'raw';
    },
    currentRouteName() {
      return this.$route.name;
    },
    visibleFields() {
      if (this.isPerformanceView) {
        return this.performanceFieldDefinitions;
      }

      return this.rawFieldDefinitions.filter((field) => {
        if (field.key === 'clicked_at') {
          return this.timestampColumn !== null;
        }

        if (field.key === 'linked_orders') {
          return true;
        }

        if (!field.column || ['clicked_at'].includes(field.key)) {
          return true;
        }

        return this.availableColumns.includes(field.column);
      });
    },
    orderFieldOptions() {
      const options = this.visibleFields
        .filter((field) => field.column)
        .map((field) => ({
          value: this.getOrderFieldValue(field),
          label: field.label || field.key,
        }))
        .filter((option) => option.value);

      return options.filter((option, index) => {
        return options.findIndex((item) => item.value === option.value) === index;
      });
    },
    noItemsView() {
      return this.isRawView
        ? {
            noResults: 'No filtering results are available!',
            noItems: 'No ad clicks found!',
          }
        : {
            noResults: 'No campaign groups match the selected filters.',
            noItems: 'No campaign performance data found!',
          };
    },
    summaryLabel() {
      return this.isRawView ? 'Total' : 'Campaign Groups';
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.errorMessage = null;

        if (this.activePage !== 1) {
          this.activePage = 1;
          return;
        }

        this.fetchDataDebounced();
      },
    },
    activeView(newValue) {
      this.errorMessage = null;
      this.rows = [];
      this.pages = 1;
      this.total = 0;
      this.orderBy = null;
      this.direction = null;
      this.resetStatsForView(newValue);
    },
    reloadParams() {
      const query = { page: this.activePage };
      if (this.activeView === 'performance') {
        query.view = this.activeView;
      }

      if (this.queryPage !== this.activePage || this.queryView !== this.activeView) {
        this.$router.push({ name: this.currentRouteName, query });
      }

      this.fetchData();
    },
  },

  beforeMount() {
    this.fetchDataDebounced = _debounce(() => {
      this.fetchData();
    }, 500);
  },

  async mounted() {
    await this.fetchData();
    this.mounted = true;

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    };
  },

  methods: {
    switchView(view) {
      if (this.activeView === view) {
        return;
      }

      this.activeView = view;
    },

    async fetchData() {
      this.loading = true;

      const url = new URL(
        this.isRawView ? this.$backend.REPORTS.AD_CLICKS : this.$backend.REPORTS.AD_CLICKS_PERFORMANCE
      );
      const params = this.isRawView ? this.getRawParams() : this.getPerformanceParams();

      Object.keys(params).forEach((key) => {
        const value = params[key];

        if (typeof value === 'undefined' || value === null || value === '') {
          return;
        }

        if (Array.isArray(value) && value.length === 0) {
          return;
        }

        url.searchParams.append(key, value);
      });

      try {
        const response = await this.$axios.get(url);

        if (this.isRawView) {
          this.applyRawResponse(response.data);
        } else {
          this.applyPerformanceResponse(response.data);
        }
      } catch (error) {
        this.rows = [];
        this.pages = 1;
        this.total = 0;
        this.availableColumns = [];
        this.timestampColumn = 'created_at';
        this.resetStatsForView(this.activeView);
        this.errorMessage = error?.response?.data?.message || 'Failed to fetch ad click report.';
      } finally {
        this.loading = false;
      }
    },

    getRawParams() {
      return {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        search: this.filters.search,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        login_user_id: this.filters.login_user_id,
        session_id: this.filters.session_id,
        linked_order: this.filters.linked_order,
        country_code: this.filters.country_code,
        device_type: this.filters.device_type,
        gad_campaignid: this.filters.gad_campaignid,
        utm_campaign: this.filters.utm_campaign,
        utm_source: this.filters.utm_source,
        utm_medium: this.filters.utm_medium,
        orderBy: this.orderBy,
        direction: this.direction,
      };
    },

    getPerformanceParams() {
      return {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        country_code: this.filters.country_code,
        device_type: this.filters.device_type,
        utm_source: this.filters.utm_source,
        utm_medium: this.filters.utm_medium,
        campaign_key: this.filters.campaign_key,
        campaign_id: this.filters.campaign_id,
        campaign_name: this.filters.campaign_name,
        client_channel: this.filters.client_channel,
        orderBy: this.orderBy,
        direction: this.direction,
      };
    },

    applyRawResponse(payload) {
      const { data, meta, stats, columns, timestamp_column } = payload;

      if (meta.current_page > meta.last_page) {
        this.activePage = 1;
        return;
      }

      this.availableColumns = columns || [];
      this.timestampColumn = timestamp_column || 'created_at';
      this.rows = data.map((item) => ({
        ...item,
        clicked_at: item[this.timestampColumn] || null,
        tracking_ids: this.buildTrackingIds(item),
        linked_orders: item.linked_orders || [],
      }));
      this.rawStats = {
        ...defaultRawStats(),
        ...stats,
      };
      this.activePage = meta.current_page;
      this.pages = meta.last_page;
      this.total = meta.total;
      this.errorMessage = null;
    },

    applyPerformanceResponse(payload) {
      const { data, meta, stats } = payload;

      if (meta.current_page > meta.last_page) {
        this.activePage = 1;
        return;
      }

      this.availableColumns = [];
      this.timestampColumn = null;
      this.rows = data;
      this.performanceStats = {
        ...defaultPerformanceStats(),
        ...stats,
      };
      this.activePage = meta.current_page;
      this.pages = meta.last_page;
      this.total = meta.total;
      this.errorMessage = null;
    },

    resetStatsForView(view) {
      if (view === 'performance') {
        this.performanceStats = defaultPerformanceStats();
        return;
      }

      this.rawStats = defaultRawStats();
    },

    paginationChange(value) {
      this.itemsPerPage = value;
      this.fetchData();
    },

    sorterValue(item) {
      const sortColumn = this.getSortColumn(item.column);
      this.orderBy = sortColumn || null;
      this.direction = item.column ? (item.asc ? 'asc' : 'desc') : null;
      this.fetchData();
    },

    orderFilterChange() {
      this.errorMessage = null;

      if (this.activePage !== 1) {
        this.activePage = 1;
        return;
      }

      this.fetchDataDebounced();
    },

    resetFilters() {
      this.filters = defaultFilters();
      this.orderBy = null;
      this.direction = null;
    },

    getOrderFieldValue(field) {
      if (this.isRawView && field.key === 'clicked_at') {
        return this.timestampColumn;
      }

      return field.column;
    },

    getSortColumn(column) {
      if (this.isRawView && column === 'clicked_at') {
        return this.timestampColumn;
      }

      if (this.isRawView && column === 'linked_orders') {
        return 'linked_order';
      }

      return column;
    },

    buildTrackingIds(item) {
      return [
        ['gclid', 'gclid'],
        ['dclid', 'dclid'],
        ['msclkid', 'msclkid'],
        ['fbclid', 'fbclid'],
        ['ttclid', 'ttclid'],
        ['gbraid', 'gbraid'],
        ['wbraid', 'wbraid'],
      ]
        .filter(([key]) => item[key])
        .map(([key, label]) => ({
          key,
          label,
          value: item[key],
        }));
    },

    formatDateTime(value) {
      if (!value) {
        return 'N/A';
      }

      const normalizedValue = typeof value === 'string' ? value.replace(' ', 'T') : value;
      const date = new Date(normalizedValue);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('nl-BE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
    },

    formatInteger(value) {
      return new Intl.NumberFormat('nl-BE', {
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },

    truncate(value, length = 42) {
      if (!value) {
        return '-';
      }

      return value.length > length ? `${value.slice(0, length - 3)}...` : value;
    },
  },
};
</script>

<style>
.filters .grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
}

.stats-cards .card .card-body {
  padding-bottom: 20px !important;
}

.tracking-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.tracking-badge {
  max-width: 100%;
}

.linked-order-links {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.view-toggle {
  display: inline-flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>

